import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import 'react-phone-input-2/lib/style.css';
import PhoneInput, { CountryData } from 'react-phone-input-2';

import { AuthenticationApi } from '../api/AuthenticationApi';
import { useErrorContext } from '../contexts/ErrorContext';
import { useUserContext } from '../contexts/UserContext';
import { CeilingLabel, CeilingLabelContainer } from '../components/styled';
import { ContactsButton, ContactsButtonSecondary } from '../components/styled/ContactsButton';
import large_logo from '../core/assets/sunshine_large.png';

import { TWebAuthRequestStatus } from 'TProtocol/authentication/messages';

const MAX_AUTH_ERRORS_BEFORE_EXIT = 3;

function Login() {
  const navigate = useNavigate();
  const errorContext = useErrorContext();
  const userContext = useUserContext();
  const [requestSent, setRequestSent] = useState(false);
  const [, setSendingRequest] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneComplete, setPhoneComplete] = useState(false);
  const [displayPhone, setDisplayPhone] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const mountedRef = useRef<boolean>();

  // effect just for tracking mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (userContext.isLoggedIn()) {
      navigate('/contacts', { replace: true });
    }
    errorContext.clearAll();
  }, [userContext]);

  // let phone: string = "";
  const buttonRef = useRef<HTMLButtonElement>(null);
  const recaptchaWrapper = useRef<HTMLDivElement>(null);
  // let displayPhone: string = "";

  // Helper function that adds a delay for x number of milliseconds before proceeding
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

  const sendWebAuthenticationRequest = async () => {
    buttonRef.current?.setAttribute('disabled', 'disabled');
    let authResponseStatus = TWebAuthRequestStatus.PENDING.toString();
    let totalAuthErrors = 0;
    const authRequestId = uuidv4();
    setErrorMessage(undefined);
    setRequestSent(true);
    const startTime = new Date().getTime();
    const REQUEST_TIMER = 180000; // only send requests for 3 min

    while (
      authResponseStatus === TWebAuthRequestStatus.PENDING.toString() &&
      totalAuthErrors < MAX_AUTH_ERRORS_BEFORE_EXIT &&
      (startTime + REQUEST_TIMER) > new Date().getTime() &&
      mountedRef.current
      ) {
      try {
        const response = await AuthenticationApi.authenticateWithMobileApp(phone, authRequestId);
        if (response.status === TWebAuthRequestStatus.SUCCESS) {
          authResponseStatus = response.status.toString();
          await userContext.handleLogin(response);
          navigate('/contacts');
          return;
        } else if (response.status === TWebAuthRequestStatus.REJECTED) {
          authResponseStatus = response.status.toString();
          handleLoginError(`Sorry, we were not able to log you in. Please make sure to login with the same number you
            onboarded to the Sunshine app with. If this problem persists, please contact Customer Support at
            support@sunshine.com.`);
          onBack();
          return;
        }
      } catch {
        totalAuthErrors++;
        // If we have reached the limit of the number of errors, return to the prior login screen
        if (totalAuthErrors === MAX_AUTH_ERRORS_BEFORE_EXIT) {
          handleLoginError(`Something unexpected went wrong trying to log in.  Please contact Customer Support at
            support@sunshine.com.`);
          onBack();
          return;
        }
      }

      // Wait 1 second before making another request
      await delay(1000);
    }
    setErrorMessage(`Oops, your login request expired. Please make sure to login with the same number you
           onboarded onto the Sunshine App with and try again.`);
    onBack();
  };

  const handleLoginError = (message: string) => {
    if (mountedRef.current) {
      errorContext.warning(message);
    }
  };

  const onBack = () => {
    setRequestSent(false);
    setSendingRequest(false);
  };

  const onPhoneChange = (newPhone: string, country: typeof CountryData, e: React.ChangeEvent<HTMLInputElement>,
    formattedPhone: string) => {
    setDisplayPhone(newPhone);
    let tempPhone = newPhone.replace(/[^\d+]/g, '');
    if (!tempPhone.startsWith('+')) {
      tempPhone = '+' + tempPhone;
    }
    setPhone(tempPhone);
    setPhoneComplete(formattedPhone.length >= country.format.length);
  };

  const handleKeypress = (event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      void sendWebAuthenticationRequest();
    }
  };

  const inputProps = {
    name: 'phone',
    required: true,
    autoFocus: true
  };

  return (
    <div>
      <SignInContent>
        <SignInLogo src={large_logo}/>
        {(!requestSent) ?
          (<div>
            <SignInHeader>Welcome to Sunshine Smart Contacts&apos; desktop companion site.</SignInHeader>
            <p>Sign in to your account with the number you provided us on the Sunshine Smart Contacts app.</p>
            <Form.Group as={CeilingLabelContainer}>
              <CeilingLabel>Phone Number</CeilingLabel>
              <SignInPhoneInput country={'us'} inputProps={inputProps} onChange={onPhoneChange}
                                value={displayPhone}
                                placeholder="Phone Number"
                                onKeyDown={handleKeypress}/>
            </Form.Group>
            <div className="text-center">
              <SignInButton ref={buttonRef} disabled={!phoneComplete}
                            id="send-confirmation-code-button" onClick={sendWebAuthenticationRequest}>Continue
              </SignInButton>
            </div>
            <div ref={recaptchaWrapper}>
              <div id="recaptcha-container"></div>
            </div>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <p>Sunshine will send you a mobile notification to verify your account.</p>
          </div>)
          :
          (<div>
            <SignInHeader>We&apos;ve pushed a login request to your device...</SignInHeader>
            <p>Please accept the notification sent to your Sunshine mobile app to sign in.</p>
            <SignInButtons>
              <ContactsButtonSecondary onClick={onBack}>Back</ContactsButtonSecondary>
            </SignInButtons>
          </div>)
        }
      </SignInContent>
    </div>
  );
}

const SignInContent = styled.div`
  max-width: 430px;
  margin: auto;
  padding-left: 24px;
  padding-right: 24px;

  // from p
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.25px;
`;

const SignInPhoneInput = styled(PhoneInput)`
  margin-bottom: 24px;
  margin-top: 24px;

  input.form-control {
    width: 100%;
    height: 40px;
    border: solid 1px #009bdf;
  }

  & div.flag-dropdown {
    outline: none;
    position: absolute;
    top: 1px;
    bottom: 1px;
    padding: 0;
    left: 1px;
    background-color: transparent;
    border: none;
  }
`;

const SignInButton = styled(ContactsButton)`
  margin-bottom: 16px;
`;


const SignInLogo = styled.img`
  max-width: 100%;
  margin-bottom: 40px;
  margin-top: 120px;
`;

const SignInHeader = styled.h3`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.44px;
`;

const ErrorMessage = styled.p`
  color: red;
  margin-top: 0.25rem;
`;

const SignInButtons = styled.div`
  float: right;
`;

export default Login;
